import { AlertCircle, ArrowUp, CheckCircle2, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Button, ModalContentWrapper, Spinner } from '@/components/ui';
import {
  useApprovalWorkflows,
  useCreateApprovalWorkflow,
  useSubmitApprovalStep,
} from '@/hooks/use-approvals';
import { cn } from '@/lib/cn';
import { handleMutationError } from '@/lib/error-handling';

interface ApprovalWorkflowTabProps {
  loanId: string;
}

export function ApprovalWorkflowTab({ loanId }: ApprovalWorkflowTabProps) {
  const { data: workflows, isLoading } = useApprovalWorkflows(loanId);
  const createWorkflow = useCreateApprovalWorkflow();
  const submitStep = useSubmitApprovalStep();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [notes, setNotes] = useState('');
  const [actionForm, setActionForm] = useState<{ workflowId: string; action: string } | null>(null);
  const [actionComments, setActionComments] = useState('');
  const [actionConditions, setActionConditions] = useState('');

  const activeWorkflow = workflows?.[0];
  const hasActiveWorkflow = activeWorkflow && activeWorkflow.status === 'PENDING';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-400">
        <Spinner size="lg" className="w-5 h-5 animate-spin mr-2" />
        Loading approval workflows…
      </div>
    );
  }

  const handleCreateWorkflow = async () => {
    try {
      await createWorkflow.mutateAsync({ loanId, notes });
      setNotes('');
      setShowCreateForm(false);
    } catch (error) {
      handleMutationError(error, 'Failed to create workflow');
    }
  };

  const handleSubmitAction = async () => {
    if (!actionForm) return;
    try {
      await submitStep.mutateAsync({
        loanId,
        workflowId: actionForm.workflowId,
        action: actionForm.action,
        comments: actionComments,
        conditions: actionConditions,
      });
      setActionForm(null);
      setActionComments('');
      setActionConditions('');
    } catch (error) {
      handleMutationError(error, 'Failed to submit approval action');
    }
  };

  const getActionIcon = (action: string) => {
    if (action === 'APPROVE') return <CheckCircle2 className="w-4 h-4 text-green-500" />;
    if (action === 'DECLINE') return <XCircle className="w-4 h-4 text-red-500" />;
    if (action === 'ESCALATE') return <ArrowUp className="w-4 h-4 text-amber-500" />;
    return <AlertCircle className="w-4 h-4 text-blue-500" />;
  };

  const getActionColor = (action: string) => {
    if (action === 'APPROVE') return 'text-green-700';
    if (action === 'DECLINE') return 'text-red-700';
    if (action === 'ESCALATE') return 'text-amber-700';
    return 'text-blue-700';
  };

  return (
    <div className="space-y-6">
      {/* Current Workflow Status */}
      {activeWorkflow && (
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Current Approval Workflow</h3>
            <span
              className={cn(
                'px-2 py-1 rounded-full text-xs font-medium',
                activeWorkflow.status === 'PENDING'
                  ? 'bg-yellow-50 text-yellow-700'
                  : activeWorkflow.status === 'APPROVED'
                    ? 'bg-green-50 text-green-700'
                    : 'bg-red-50 text-red-700',
              )}
            >
              {activeWorkflow.status}
            </span>
          </div>

          {/* Timeline */}
          <div className="space-y-4">
            {activeWorkflow.steps?.map((step: any, i: number) => (
              <div key={i} className="flex gap-4">
                <div className="flex flex-col items-center">
                  {getActionIcon(step.action)}
                  {i < (activeWorkflow.steps?.length || 0) - 1 && (
                    <div className="w-0.5 h-12 bg-gray-200 my-2" />
                  )}
                </div>
                <div className="flex-1 pt-0.5">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium text-gray-900">{step.action}</p>
                    <span className={cn('text-xs font-medium', getActionColor(step.action))}>
                      Tier {step.tier}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {step.approvedBy?.firstName} {step.approvedBy?.lastName}
                  </p>
                  <p className="text-xs text-gray-400">
                    {step.approvedAt
                      ? new Date(step.approvedAt).toLocaleDateString('en-AU')
                      : 'Pending'}
                  </p>
                  {step.comments && (
                    <p className="text-sm text-gray-600 mt-2 border-l-2 border-gray-200 pl-3">
                      {step.comments}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pending Action Buttons */}
          {activeWorkflow.status === 'PENDING' && (
            <div className="mt-6 pt-4 border-t border-gray-200 flex gap-2">
              <button
                onClick={() => setActionForm({ workflowId: activeWorkflow.id, action: 'APPROVE' })}
                className="flex-1 px-3 py-2 bg-green-50 border border-green-200 text-green-700 rounded-md text-sm font-medium hover:bg-green-100 transition-colors"
              >
                Approve
              </button>
              <button
                onClick={() => setActionForm({ workflowId: activeWorkflow.id, action: 'DECLINE' })}
                className="flex-1 px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm font-medium hover:bg-red-100 transition-colors"
              >
                Decline
              </button>
              <button
                onClick={() => setActionForm({ workflowId: activeWorkflow.id, action: 'ESCALATE' })}
                className="flex-1 px-3 py-2 bg-amber-50 border border-amber-200 text-amber-700 rounded-md text-sm font-medium hover:bg-amber-100 transition-colors"
              >
                Escalate
              </button>
              <button
                onClick={() =>
                  setActionForm({ workflowId: activeWorkflow.id, action: 'REQUEST_INFO' })
                }
                className="flex-1 px-3 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-md text-sm font-medium hover:bg-blue-100 transition-colors"
              >
                Request Info
              </button>
            </div>
          )}
        </div>
      )}

      {/* Start Workflow Button */}
      {!hasActiveWorkflow && !showCreateForm && (
        <div className="flex flex-col items-center justify-center bg-gray-50 rounded-lg border border-gray-200 p-5 text-center">
          <p className="text-sm text-gray-600 mb-3">No active approval workflow</p>
          <Button
            variant="primary"
            onClick={() => setShowCreateForm(true)}
            className="w-fit px-4 py-2 text-white rounded-md text-sm font-medium hover: transition-colors"
          >
            Start Approval Workflow
          </Button>
        </div>
      )}

      {/* Create Workflow Form */}
      {showCreateForm && !hasActiveWorkflow && (
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Create Approval Workflow</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (optional)
              </label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Add any notes for the approval workflow…"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                rows={3}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="primary"
                onClick={handleCreateWorkflow}
                disabled={createWorkflow.isPending}
                className="w-fit px-4 py-2 text-white rounded-md text-sm font-medium hover: disabled:opacity-50 transition-colors"
              >
                {createWorkflow.isPending ? 'Creating…' : 'Create Workflow'}
              </Button>
              <button
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Form Modal */}
      {actionForm && (
        <ModalContentWrapper
          open={Boolean(actionForm)}
          onClose={() => {
            setActionForm(null);
            setActionComments('');
            setActionConditions('');
          }}
          title={`${actionForm.action} Loan`}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Comments</label>
              <textarea
                value={actionComments}
                onChange={e => setActionComments(e.target.value)}
                placeholder="Add your comments…"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                rows={3}
              />
            </div>
            {actionForm.action === 'APPROVE' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Conditions (if any)
                </label>
                <textarea
                  value={actionConditions}
                  onChange={e => setActionConditions(e.target.value)}
                  placeholder="Add any conditions for approval…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                />
              </div>
            )}
            <div className="flex gap-2">
              <Button
                variant="primary"
                onClick={handleSubmitAction}
                disabled={submitStep.isPending}
                className="w-fit flex-1 px-4 py-2 text-white rounded-md text-sm font-medium hover: disabled:opacity-50 transition-colors"
              >
                {submitStep.isPending ? 'Submitting…' : 'Submit'}
              </Button>
              <button
                onClick={() => setActionForm(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </ModalContentWrapper>
      )}
    </div>
  );
}
